import { useState, type JSX } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Notice } from '@dxtech/shared';
import { Modal } from '../ui/common.js';
import { RichTextEditor } from '../ui/RichText.js';
import { api } from '../api/client.js';
import { kstToday } from '../lib/format.js';

/** 공지 등록/수정 모달 (Admin 전용). notice 전달 시 수정 모드. */
export function NoticeFormModal(
  { notice, onClose, onDone }:
  { notice?: Notice | null; onClose: () => void; onDone: (msg: string) => void },
): JSX.Element {
  const qc = useQueryClient();
  const editing = Boolean(notice);
  const [title, setTitle] = useState(notice?.title ?? '');
  const [noticeDate, setNoticeDate] = useState(notice?.noticeDate ?? kstToday());
  const [pinned, setPinned] = useState(notice?.pinned ?? false);
  const [bodyHtml, setBodyHtml] = useState(notice?.bodyHtml ?? '');
  const [busy, setBusy] = useState(false);

  const canSubmit = title.trim().length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(noticeDate);

  const submit = async (): Promise<void> => {
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      const body = { title: title.trim(), bodyHtml, noticeDate, pinned };
      if (editing && notice) await api.updateNotice(notice.id, body);
      else await api.createNotice(body);
      await qc.invalidateQueries({ queryKey: ['notices'] });
      onDone(editing ? '공지를 수정했습니다' : '공지를 등록했습니다');
      onClose();
    } catch (e) {
      onDone(e instanceof Error ? e.message : '저장 실패');
      setBusy(false);
    }
  };

  return (
    <Modal width={620} onClose={onClose}>
      <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{editing ? '공지 수정' : '공지 등록'}</div>
          <button title="닫기" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6B7280', fontSize: 26, lineHeight: 1 }}>×</button>
        </div>

        <label style={{ ...field, marginBottom: 14 }}><span style={lbl}>제목</span>
          <input className="dx-input" placeholder="공지 제목" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>

        <div style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'flex-end' }}>
          <label style={{ ...field, flex: '0 0 180px' }}><span style={lbl}>게시일</span>
            <input type="date" className="dx-input" value={noticeDate} onChange={(e) => setNoticeDate(e.target.value)} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', height: 38, cursor: 'pointer' }}>
            <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
            상단 고정
          </label>
        </div>

        <div style={{ ...field, marginBottom: 6 }}><span style={lbl}>내용</span>
          <RichTextEditor value={bodyHtml} onChange={setBodyHtml} />
        </div>

        <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 8 }}>
          ※ 게시일이 오늘 이후인 공지는 개인 메인화면 상단에 표시됩니다.
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button className="dx-btn dx-btn-ghost" style={{ flex: 1 }} onClick={onClose}>취소</button>
          <button className="dx-btn dx-btn-primary" style={{ flex: 1 }} disabled={busy || !canSubmit} onClick={submit}>{busy ? '저장 중…' : editing ? '수정 저장' : '등록'}</button>
        </div>
      </div>
    </Modal>
  );
}

const field = { display: 'flex', flexDirection: 'column', gap: 6 } as const;
const lbl = { fontSize: 12, fontWeight: 600, color: '#6B7280' } as const;
